/**
 * Patient-side map: the same board, reached with a token instead of an account.
 *
 * The participant id is kept in this browser so a reconnect — a dropped
 * connection, a reloaded tab — comes back as the same person rather than as a
 * second one. It is an identity for attribution, not a credential: the token is
 * what grants access, and the server checks the pairing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SystemicMapAPI } from "../infrastructure/api/SystemicMap.api";
import type { Operation } from "../domain/operations";
import type { PublicSystemicMap } from "../domain/types";
import { useBoardPresence } from "./useBoardPresence";
import { useBoardChannel, useTransport, type LoadStatus } from "./useSystemicMap";

const PARTICIPANT_STORAGE_PREFIX = "systemicMap:participant:";

/** Why the board will not open. Each one needs different words on screen. */
export type TokenError = "unusable" | "network" | null;

const storageKey = (token: string) => `${PARTICIPANT_STORAGE_PREFIX}${token}`;

const readStoredParticipant = (token: string): string | undefined => {
    try {
        return localStorage.getItem(storageKey(token)) ?? undefined;
    } catch {
        // A private window, or storage blocked. Joining still works; it just
        // mints a new identity, which is better than refusing to open.
        return undefined;
    }
};

const storeParticipant = (token: string, participantId: string): void => {
    try {
        localStorage.setItem(storageKey(token), participantId);
    } catch {
        /* not fatal — see above */
    }
};

export function usePatientMap(token: string | undefined, displayName = "") {
    const [participantId, setParticipantId] = useState<string | null>(null);
    const [joinStatus, setJoinStatus] = useState<LoadStatus>("idle");
    const [tokenError, setTokenError] = useState<TokenError>(null);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setJoinStatus("loading");
        setTokenError(null);

        (async () => {
            try {
                const { data } = await SystemicMapAPI.join(
                    token,
                    displayName,
                    readStoredParticipant(token),
                );
                if (cancelled) return;
                storeParticipant(token, data.participant_id);
                setParticipantId(data.participant_id);
                setJoinStatus("ready");
            } catch (error) {
                if (cancelled) return;
                // 403 means the link is spent, revoked or unknown — the three
                // are deliberately indistinguishable from out here.
                const status = (error as { response?: { status?: number } })?.response
                    ?.status;
                setTokenError(status === 403 || status === 404 ? "unusable" : "network");
                setJoinStatus("error");
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [token, displayName]);

    const read = useCallback(async () => {
        const { data } = await SystemicMapAPI.publicGet(token!, participantId!);
        return data;
    }, [token, participantId]);

    const write = useCallback(
        async (operations: Operation[]) => {
            const { data } = await SystemicMapAPI.publicApplyOperations(
                token!,
                participantId!,
                operations,
            );
            return data;
        },
        [token, participantId],
    );

    const requestTicket = useMemo(
        () =>
            token && participantId
                ? async () =>
                      (await SystemicMapAPI.publicWsTicket(token, participantId)).data
                          .ticket
                : null,
        [token, participantId],
    );
    const transport = useTransport<PublicSystemicMap>(
        Boolean(token && participantId),
        read,
        write,
    );
    const refreshRef = useRef<() => void>(() => {});
    const presence = useBoardPresence(requestTicket, () => refreshRef.current());
    const channel = useBoardChannel<PublicSystemicMap>(transport, presence);
    refreshRef.current = () => void channel.refresh();

    return {
        ...channel,
        presence,
        participantId,
        joinStatus,
        tokenError,
        // Until the join lands there is no board to report on, so the page
        // shows the join's state rather than an "error" from a poll that has
        // not been allowed to run yet.
        status: joinStatus === "ready" ? channel.status : joinStatus,
    };
}
